import { useEffect, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { PlanProvider, createPlanApiClient } from '@onxite/plan-sdk';
import { AppRouter } from './router/routes';
import { Toaster } from './components/ui/Toast';
import { queryClient } from './lib/api/queryClient';
import { useAuthStore } from './lib/auth/authStore';
import { useBrandContextStore } from './lib/branding/brandContextStore';

function FatalErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--color-xk-bg)] px-6 text-center text-[var(--color-xk-text)]">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="max-w-md text-sm opacity-70">
        The error was reported automatically. Try reloading; if it persists, contact support.
      </p>
      <button
        type="button"
        onClick={() => {
          resetError();
          window.location.reload();
        }}
        className="rounded-md bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-medium text-white"
      >
        Reload
      </button>
    </div>
  );
}

export function App() {
  // Build the SDK's API client once. `getToken` reads the latest token from
  // Zustand on each request, so token refreshes flow through automatically.
  // VITE_API_URL already includes `/api/v1` (matches axios client baseURL),
  // so override the SDK's default prefix to '' to avoid doubling the segment.
  const planClient = useMemo(
    () =>
      createPlanApiClient({
        baseURL: import.meta.env.VITE_API_URL ?? 'http://localplatform.com:3000/api/v1',
        prefix: '',
        getToken: () => useAuthStore.getState().token,
      }),
    [],
  );

  // Cross-store cleanup on logout. Without this the previous user's
  // selected brand and React Query cache survive the auth change — the
  // next user briefly sees stale data (e.g. brand profile from the
  // previous account) until queries refetch. The subscription fires on
  // EVERY logout path, not just the sign-out button.
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state, prevState) => {
      if (prevState.token && !state.token) {
        useBrandContextStore.getState().clear();
        queryClient.clear();
      }
    });
    return unsubscribe;
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <FatalErrorFallback resetError={resetError} />}>
      <QueryClientProvider client={queryClient}>
        {/* upgradeUrl drives the plan-alert banner CTA (Caso 15). The
            SDK default `/billing/plans` is not a route in this app — it
            would fall through the catch-all to `/`. Point it at the
            authenticated billing page. */}
        <PlanProvider apiClient={planClient} upgradeUrl="/settings/billing">
          <AppRouter />
          <Toaster />
        </PlanProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
